import React, {useRef} from 'react';

interface FileUploadProps {
  id?: string;
  value?: string;
  placeholder?: string;
  accept?: string;
  onChange: (fileName: string, file: File) => void;
  error?: boolean;
  disabled?: boolean;
  className?: string;
  previewUrl?: string;
  ariaLabel?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  id,
  value,
  placeholder = 'Click here to upload',
  accept = 'image/*',
  onChange,
  error = false,
  disabled = false,
  className = '',
  previewUrl,
  ariaLabel,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onChange(file.name, file);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const hasExistingImage = previewUrl && previewUrl.trim() !== '';

  const getBorderClass = () => {
    if (error) return 'border-red-500';
    return 'border-gray-300';
  };

  const getButtonClass = () => {
    if (disabled) return 'text-gray-400 bg-gray-200 cursor-not-allowed';
    return 'text-blue-500 bg-blue-50 hover:bg-blue-100';
  };

  const getUploadAreaClass = () => {
    if (disabled) return 'bg-gray-100 cursor-not-allowed border-gray-200';
    if (error) return 'border-red-500 hover:bg-red-50';
    return 'border-gray-300 hover:bg-gray-50';
  };

  const getTextClass = () => {
    if (value) return 'text-gray-900';
    return 'text-gray-500';
  };

  const getTabIndex = () => {
    return disabled ? -1 : 0;
  };

  const getAriaLabel = () => {
    if (ariaLabel) return ariaLabel;
    return `Upload ${accept === 'image/*' ? 'image' : 'file'}`;
  };

  const getChangeAriaLabel = () => {
    return ariaLabel || 'Change current image';
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        id={id}
        onChange={handleFileChange}
        disabled={disabled}
        aria-label={ariaLabel || `Upload ${accept === 'image/*' ? 'image' : 'file'}`}
      />

      {hasExistingImage ? (
        <div className={`border rounded-lg overflow-hidden ${getBorderClass()}`}>
          <div className="aspect-video w-full bg-gray-100 flex items-center justify-center">
            <img src={previewUrl} alt="Preview" className="max-w-full max-h-full object-contain" />
          </div>
          <div className="p-3 bg-gray-50 flex justify-between items-center">
            <span className="text-sm text-gray-600">Current image</span>
            <button
              type="button"
              onClick={handleClick}
              disabled={disabled}
              aria-label={getChangeAriaLabel()}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${getButtonClass()}`}>
              Change
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          role="button"
          tabIndex={getTabIndex()}
          onKeyDown={e => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
              e.preventDefault();
              handleClick();
            }
          }}
          aria-label={getAriaLabel()}
          className={`flex items-center justify-between w-full px-4 py-3 border rounded-lg cursor-pointer transition-colors duration-150 ${getUploadAreaClass()}`}>
          <span className={getTextClass()}>{value || placeholder}</span>
          <span className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${getButtonClass()}`}>Upload</span>
        </div>
      )}
    </div>
  );
};
